import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "const/axios";
import { API_URL, API_URL_PUBLIC, studentInitialValue } from "const/constants";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { countUnreadAccountAreaNotifications } from "utils/accountNotificationUtils";
import {
  canAccessPlatformOrganisations,
  hasPermission,
  isOrgSuperAdmin,
} from "utils/permissionUtils";
import { P } from "const/permissions";
import {
  createRefetchPaginatedClients,
  getClientData,
  useArchivedClients,
  usePaginatedClients,
} from "utils/clientService";
import { createNewAccountDetail } from "views/admin/Accounts/accountUtils";

const useStateAndActions = () => {
  const queryClient = useQueryClient();
  const [token, setToken] = useState(localStorage.getItem("token"));
  // Read userDetail from localStorage dynamically to get fresh data after login
  const getUserDetail = () => {
    try {
      const stored = localStorage.getItem("userDetail");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };
  const userDetail = getUserDetail();
  const canReadNotifications = hasPermission(P.NOTIFICATIONS.READ);
  const canReadReminders = hasPermission(P.REMINDERS.READ);
  const canReadAppointments = hasPermission(P.APPOINTMENTS.READ);
  const canReadDashboardStats = hasPermission(P.DASHBOARD.READ);
  const [selectedVisaTab, setSelectedVisaTab] = useState({
    label: "All",
    value: "all",
  });
  const [combinedClientData, setCombinedClientData] = useState([]);
  const isAdminOrSuperAdmin = isOrgSuperAdmin() || userDetail?.type === "ADMIN";
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    searchText: "",
    caseOfficer: isAdminOrSuperAdmin ? "" : userDetail?.name?.slice(0, 4),
    createdMonth: "",
    createdYear: "",
    sortBy: "visaExpiry",
    sortDirection: "ASC",
    active: "Y",
  });

  const [notificationPagination, setNotificationPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    sortBy: "createdDate",
    sortDirection: "DESC",
    readEnum: "ALL",
    tag: "",
  });
  const [notificationPaginationForNavbar, setNotificationPaginationForNavbar] = useState({
    pageIndex: 0,
    pageSize: 15,
    sortBy: "createdDate",
    sortDirection: "DESC",
    readEnum: "ALL",
    tag: "",
  });
  const [notificationPaginationForAccounts, setNotificationPaginationForAccounts] = useState({
    pageIndex: 0,
    pageSize: 15,
    sortBy: "createdDate",
    sortDirection: "DESC",
    readEnum: "ALL",
    tag: "",
    module: "account",
  });

  const [invoicePagination, setInvoicePagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    sortBy: "issueDate",
    search: "",
    status: null,
    paymentStatus: null,
    sortDirection: "DESC",
  });

  /** Last selected tab per client on View Client (keyed by client id) */
  const [clientViewTabByClientId, setClientViewTabByClientId] = useState({});

  const setClientViewTab = useCallback((clientId, tabKey) => {
    if (clientId == null || !tabKey) return;
    setClientViewTabByClientId((prev) => ({ ...prev, [String(clientId)]: tabKey }));
  }, []);

  const [accountPagination, setAccountPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    sortBy: "updatedDate",
    sortDirection: "DESC",
    search: "",
    status: null,
    effective: false,
  });

  const [providerPagination, setProviderPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
    sortBy: "",
    sortDirection: "DESC",
    search: "",
  });
  const [isAccountsPageActive, setIsAccountsPageActive] = useState(false);
  const [isNotificationsPageActive, setIsNotificationsPageActive] = useState(false);

  const fetchAccountsPage = async (params) => {
    const { pageIndex, pageSize, search, status, sortBy, sortDirection, effective } = params;
    const res = await axios.get(`accounts/aggregated`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      params: {
        page: pageIndex,
        size: pageSize,
        search,
        status,
        sortBy,
        sortDirection,
        effective: effective ? true : undefined,
      },
    });
    const raw = res?.data?.data;
    if (Array.isArray(raw)) {
      return { content: raw, totalItems: raw.length };
    }
    return {
      content: Array.isArray(raw?.content)
        ? raw.content
        : Array.isArray(raw?.items)
        ? raw.items
        : [],
      totalItems: Number(raw?.totalItems) || 0,
    };
  };

  const {
    data: accountsPage = { content: [], totalItems: 0 },
    isFetching: isAccountsListLoading,
    refetch: refetchAccountList,
  } = useQuery(["accounts", accountPagination], () => fetchAccountsPage(accountPagination), {
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    enabled: false,
  });

  const accountsList = accountsPage.content;
  const accountsTotalItemsResolved = accountsPage.totalItems;

  const [rplList, setRPLList] = useState([]);
  const [accountData, setAccountData] = useState([]);
  const [profitLossReport, setProfitLossReport] = useState(null);
  const [isProfitLossLoading, setIsProfitLossLoading] = useState(false);
  const [notificationTriggered, setNotificationTriggered] = useState(false);
  // const [notificationsList, setNotificationsList] = useState([]);
  const [notificationClicked, setNotificationClicked] = useState({
    state: false,
    id: null,
  });
  const [accountDataLoading, setAccountDataLoading] = useState(false);
  const accountFetchSeqRef = useRef(0);
  const [accountDetails, setAccountDetails] = useState([
    {
      uuid: crypto.randomUUID(),
      module: "",
      agreedAmount: "",
      cost: "",
      dueAmount: "",
      referral: "",
      profitLoss: "",
      paidAmount: "",
      isCommissionClaimed: false,
      isBonusClaimed: false,
      admissionValues: studentInitialValue,
      admissionDetails: [],
    },
  ]);

  const [providerAccountDetails, setProviderAccountDetails] = useState([
    {
      id: null,
      uuid: crypto.randomUUID(),
      providerId: "",
      agentName: "",
      noOfStudents: null,
      totalCommission: null,
      totalBonus: null,
      totalGST: null,
      nextReminder: format(new Date(), "yyyy-MM-dd"),
      intakeDetails: [],
      commissionDetails: [{ commission: "", bonus: "", total: "" }],
    },
  ]);
  const [providerData, setProviderData] = useState([]);
  const [publicUrl, setPublicUrl] = useState("");
  const [publicUrlError, setPublicUrlError] = useState("");
  const normalizeNotificationTag = (tag) => {
    if (!tag || tag === "ALL") return "";
    return tag;
  };

  const getNotificationData = async (queryParams = {}) => {
    const {
      page = notificationPagination?.pageIndex || 0,
      size = notificationPagination?.pageSize || 20,
      sortBy = notificationPagination?.sortBy || "createdDate",
      sortDirection = notificationPagination?.sortDirection || "DESC",
      readEnum = notificationPagination?.readEnum || "ALL",
      tag = notificationPagination?.tag || "",
    } = queryParams;
    const params = { page, size, sortBy, sortDirection, readEnum };
    const normalizedTag = normalizeNotificationTag(tag);
    if (normalizedTag) params.tag = normalizedTag;
    const query = new URLSearchParams(params);

    const res = await axios.get(`notification/page?${query}`);
    return res?.data?.data || [];
  };

  const {
    data: notificationsList = [],
    isFetching: isNotificationLoading,
    refetch: refetchNotifications,
  } = useQuery(
    ["notification", notificationPagination],
    () => getNotificationData(notificationPagination),
    {
      refetchOnWindowFocus: false,
      enabled: !!token && canReadNotifications && isNotificationsPageActive,
      refetchOnMount: true,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const refetchNotificationData = (params = {}) => {
    // queryClient.invalidateQueries({ queryKey: ["notification"] });
    // If params are provided, update pagination first, then invalidate

    if (Object.keys(params).length > 0) {
      const nextParams = { ...params };
      if ("tag" in nextParams) {
        nextParams.tag = normalizeNotificationTag(nextParams.tag);
      }
      setNotificationPagination((prev) => ({ ...prev, ...nextParams }));
    }
    // Invalidate all notification queries (regardless of pagination) to force refetch
    queryClient.fetchQuery({
      queryKey: ["notification"],
      queryFn: () => getNotificationData(params),
    });
  };

  const getPaginatedNotifications = async (defaults, queryParams = {}) => {
    const {
      page = defaults?.pageIndex || 0,
      size = defaults?.pageSize || 20,
      sortBy = defaults?.sortBy || "createdDate",
      sortDirection = defaults?.sortDirection || "DESC",
      readEnum = defaults?.readEnum || "ALL",
      tag = defaults?.tag || "",
      module = defaults?.module || "",
    } = queryParams;
    const params = { page, size, sortBy, sortDirection, readEnum };
    const normalizedTag = normalizeNotificationTag(tag);
    if (normalizedTag) params.tag = normalizedTag;
    if (module) params.module = module;
    const query = new URLSearchParams(params);

    const res = await axios.get(`notification/page?${query}`);
    return res?.data?.data || [];
  };

  const getNotificationDataForNavbar = async (queryParams = {}) =>
    getPaginatedNotifications(notificationPaginationForNavbar, queryParams);

  const getNotificationDataForAccounts = async (queryParams = {}) =>
    getPaginatedNotifications(notificationPaginationForAccounts, queryParams);

  const {
    data: notificationsListForNavbar = [],
    isFetching: isNotificationLoadingForNavbar,
    refetch: refetchNotificationsForNavbar,
  } = useQuery(
    ["notificationForNavbar", notificationPaginationForNavbar],
    () => getNotificationDataForNavbar(notificationPaginationForNavbar),
    {
      refetchOnWindowFocus: false,
      enabled: !!token && canReadNotifications,
      refetchOnMount: true,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const {
    data: notificationsListForAccounts = [],
    isFetching: isNotificationLoadingForAccounts,
    isFetched: isNotificationsForAccountsFetched,
    refetch: refetchNotificationsForAccounts,
  } = useQuery(
    ["notificationForAccounts", notificationPaginationForAccounts],
    () => getNotificationDataForAccounts(notificationPaginationForAccounts),
    {
      refetchOnWindowFocus: false,
      // Only the Accounts page reads this — it already fetches on its own mount
      // (see the initialLoadDoneRef effect in Accounts/index.js).
      enabled: false,
      refetchOnMount: true,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const refetchNotificationDataForNavbar = (params = {}) => {
    if (Object.keys(params).length > 0) {
      const nextParams = { ...params };
      if ("tag" in nextParams) {
        nextParams.tag = normalizeNotificationTag(nextParams.tag);
      }
      setNotificationPaginationForNavbar((prev) => ({ ...prev, ...nextParams }));
      // useQuery refetches when notificationPaginationForNavbar (query key) changes.
      return;
    }
    refetchNotificationsForNavbar();
  };

  const refetchNotificationDataForAccounts = (params = {}) => {
    if (Object.keys(params).length > 0) {
      const nextParams = { ...params };
      if ("tag" in nextParams) {
        nextParams.tag = normalizeNotificationTag(nextParams.tag);
      }
      setNotificationPaginationForAccounts((prev) => ({ ...prev, ...nextParams }));
      return;
    }
    refetchNotificationsForAccounts();
  };

  // Use client service hooks
  const {
    data: paginatedClientList,
    isFetching: isPaginatedClientLoading,
    refetch: refetchPaginatedClientQuery,
  } = usePaginatedClients(pagination, {
    // Only Clients/index.js reads this — it already self-fetches via refetchPaginatedClient()
    // on its own mount. Not needed on every login/page (MyClients.js dashboard widget uses
    // its own separate usePaginatedClients call with a caseOfficer filter, unaffected).
    enabled: false,
  });

  const refetchPaginatedClient = createRefetchPaginatedClients(
    queryClient,
    pagination,
    setPagination,
    refetchPaginatedClientQuery
  );

  const getProfitLossReport = async (params = {}) => {
    setIsProfitLossLoading(true);
    try {
      const res = await axios.get(`profit-loss/report`, {
        params: {
          from: params.from || undefined,
          to: params.to || undefined,
          module: params.module || undefined,
          caseOfficer: params.caseOfficer || undefined,
          claimFilter: params.claimFilter || undefined,
          search: params.search || undefined,
          effective: params.effective ? true : undefined,
          view: params.view || undefined,
          page: params.page ?? undefined,
          size: params.size ?? undefined,
          sortBy: params.sortBy || undefined,
          sortDirection: params.sortDirection || undefined,
        },
      });
      setProfitLossReport(res?.data?.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Server Error loading Profit/Loss report");
    } finally {
      setIsProfitLossLoading(false);
    }
  };

  const getReferralData = async () => {
    const res = await axios.get(`referrals/summary`);
    return res?.data?.data;
  };

  const {
    data: referralList,
    isFetching: isReferralListLoading,
    refetch: refetchReferral,
  } = useQuery(["referral"], getReferralData, {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const getReferralClientsEntries = async (id) => {
    const res = await axios.get(`referrals/${id}/clients`);
    return res?.data?.data || []; // Return empty array if undefined
  };

  const { data: referralClientsEntriesList, isFetching: isReferralClientsEntriesListLoading } =
    useQuery(["referralClients"], () => getReferralClientsEntries(), {
      refetchOnWindowFocus: false,
      enabled: false,
    });

  const refetchReferralClientsEntries = (id) => {
    // queryClient.invalidateQueries({ queryKey: ["referralClients"] });
    queryClient.fetchQuery({
      queryKey: ["referralClientsEntries"],
      queryFn: () => getReferralClientsEntries(id),
    });
  };

  const getOrganisationData = async () => {
    const res = await axios.get(`organization`);
    return res?.data?.data;
  };

  const getMembershipOrganisations = async () => {
    try {
      const res = await axios.get(`auth/memberships`);
      const memberships = res?.data?.data || [];
      return memberships.map((m) => ({
        id: m.orgId,
        name: m.orgName,
        membershipId: m.membershipId,
        role: m.role,
        organizationDeleted: m.organizationDeleted,
      }));
    } catch {
      return [];
    }
  };

  const fetchConsultancyList = async () => {
    if (canAccessPlatformOrganisations()) {
      return getOrganisationData();
    }
    return getMembershipOrganisations();
  };

  const {
    data: consultancyList,
    isFetching: isConsultancyListLoading,
    refetch: refetchConsultancy,
  } = useQuery(["organization"], fetchConsultancyList, {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const {
    data: clientList,
    refetch: refetchClient,
    isFetching: isClientLoading,
    error: clientError,
  } = useQuery(["client"], getClientData, {
    refetchOnWindowFocus: false,
    // Full org-wide client roster (unpaginated) — heavy. Only fetched on-demand by
    // consuming pages/modals now (each guards with `if (!clientList?.length) refetchClient()`
    // or an equivalent scoped fetch), not eagerly on every login.
    enabled: false,
  });

  // Bounded (backend filters to a small matching set), so no pagination needed here — avoids
  // Dashboard/ClientWithVisaExpiringSoon having to filter the entire org client list client-side.
  const getClientsVisaExpiring = async () => {
    const res = await axios.get(`clients/visa-expiring`, { params: { days: 30 } });
    return res?.data?.data || [];
  };
  const {
    data: clientsVisaExpiring = [],
    isFetching: isClientsVisaExpiringLoading,
    refetch: refetchClientsVisaExpiring,
  } = useQuery(["clients-visa-expiring"], getClientsVisaExpiring, {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const getDashboardStats = async () => {
    const res = await axios.get(`dashboard/stats`, { params: { visaExpiringDays: 30 } });
    return res?.data?.data;
  };
  const {
    data: dashboardStats,
    isFetching: isDashboardStatsLoading,
    refetch: refetchDashboardStats,
  } = useQuery(["dashboardStats"], getDashboardStats, {
    refetchOnWindowFocus: false,
    // Fetched once on login, not on Dashboard mount: the sidebar badges read from it, and a
    // count that only appears after you happen to visit the page it counts is no reminder.
    enabled: !!token && canReadDashboardStats,
  });

  const unreadAccountNotificationsCount = useMemo(() => {
    if (isNotificationsForAccountsFetched) {
      return (
        notificationsListForAccounts?.unreadCount ??
        countUnreadAccountAreaNotifications(notificationsListForAccounts)
      );
    }
    return dashboardStats?.unreadAccountNotificationsCount ?? 0;
  }, [notificationsListForAccounts, isNotificationsForAccountsFetched, dashboardStats]);

  const {
    data: checkInData,
    isLoading: isCheckInDataLoading,
    refetch: refetchCheckInData,
  } = useQuery({
    queryKey: ["check-in-data"],
    queryFn: async () => {
      const res = await axios.get(`/client-form`);
      return res?.data?.data;
    },

    refetchOnWindowFocus: false,
    // Only ClientWithVisaExpiringSoon.js reads this — it fetches on its own mount.
    enabled: false,
    refetchOnMount: true,
  });
  // Use client service hook
  const { data: archivedClientList, refetch: refetchArchivedClient } = useArchivedClients({
    enabled: false,
  });

  const getUniData = async () => {
    const res = await axios.get(`university`);

    return res?.data?.data;
  };

  const {
    data: uniData = [],
    isFetching: isUniLoading,
    refetch: refetchUniData,
  } = useQuery(["university"], getUniData, {
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  const getCourseData = async () => {
    const res = await axios.get(`courses/with-offerings`);
    return res?.data?.data;
  };
  const { data: courseList, refetch: refetchCourseList } = useQuery(["course"], getCourseData, {
    refetchOnWindowFocus: false,
    // Full org-wide course-with-offerings payload (unpaginated) — heavy. Only fetched
    // on-demand by consuming pages/modals now, not eagerly on every login.
    enabled: false,
    retry: false,
  });

  const getReminderData = async () => {
    const res = await axios.get(`/client-reminders`);
    return res?.data?.data;
  };
  const {
    data: reminderList,
    isFetching: isReminderLoading,
    refetch: refetchReminderList,
  } = useQuery(["reminder"], getReminderData, {
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
    // Long-lived — ReminderPopover (always mounted), Reminders/index.js, and the Dashboard
    // widget all share this. Explicit refetchReminderList() calls after mutations (toggle
    // complete, add/edit/delete, mark-as-read) keep it correct without refetching on nav.
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const getAppointmentData = async () => {
    const res = await axios.get(`/appointments`);
    return res?.data?.data;
  };
  const {
    data: appointmentList,
    isFetching: isAppointmentLoading,
    refetch: refetchAppointmentList,
  } = useQuery(["appointment"], getAppointmentData, {
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const getRPLCourse = async () => {
    const res = await axios.get(`rpl-course`);
    return res?.data?.data;
  };
  const {
    data: rplCourseList,
    refetch: refetchRPLCourseList,
    isFetching: isRPLCourseListFetching,
  } = useQuery(["rplCourse"], getRPLCourse, {
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  const getStudent = async () => {
    const res = await axios.get(`student`);
    return res?.data?.data;
  };
  const {
    data: studentList,
    refetch: refetchStudent,
    isFetching: isStudentLoading,
  } = useQuery(["student"], getStudent, {
    refetchOnWindowFocus: false,
    retry: false,
    // No page reads the full org-wide admissions list anymore — ClientAccounts.js and
    // AddAccounts.js now use the scoped useClientAdmissions(clientId) hook instead
    // (utils/studentService.js). Kept registered (not deleted) only in case something
    // still calls refetchStudent(); nothing currently does.
    enabled: false,
  });

  const getUsers = async () => {
    const res = await axios.get(`users`, {});
    return res?.data?.data;
  };
  const {
    data: usersList,
    isFetching: isUsersListLoading,
    refetch: refetchUsers,
  } = useQuery(["users"], getUsers, {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: isAdminOrSuperAdmin,
  });

  const getInvoiceList = async (params) => {
    const { pageIndex, pageSize, search, status, paymentStatus, sortBy, sortDirection } = params;
    const res = await axios.get(`invoice`, {
      params: {
        page: pageIndex,
        size: pageSize,
        search,
        status,
        paymentStatus,
        sortBy,
        sortDirection,
      },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return res?.data?.data;
  };
  const {
    data: invoiceList,
    isFetching: isInvoiceListLoading,
    refetch: refetchInvoiceList,
  } = useQuery(["invoice", invoicePagination], () => getInvoiceList(invoicePagination), {
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    // Only Invoice/index.js + AddInvoice.js read this — both self-fetch on mount when
    // empty.
    enabled: false,
  });

  const fetchInvoiceList = (params) =>
    queryClient.fetchQuery({
      queryKey: ["invoice", params],
      queryFn: () => getInvoiceList(params),
      staleTime: 0,
    });

  const getInvoiceSummary = async () => {
    const res = await axios.get(`invoice/summary`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return res?.data?.data;
  };

  const {
    data: invoiceSummary,
    isFetching: isInvoiceSummaryLoading,
    isFetched: isInvoiceSummaryFetched,
    refetch: refetchInvoiceSummary,
  } = useQuery(["invoiceSummary"], getInvoiceSummary, {
    refetchOnWindowFocus: false,
    // Only Invoice/index.js fetches this — it self-fetches on its own mount. The sidebar
    // badge reads whatever it left behind, and falls back to the dashboard stats.
    enabled: false,
  });

  // Unpaid + partially paid, both excluding drafts. The Invoice page's own summary is the live
  // figure once that page has loaded it; until then the login-time stats count stands in.
  const outstandingInvoicesCount = useMemo(() => {
    if (isInvoiceSummaryFetched && invoiceSummary) {
      return (invoiceSummary.unpaidInvoicesCount ?? 0) + (invoiceSummary.partialInvoicesCount ?? 0);
    }
    return dashboardStats?.unpaidInvoicesCount ?? 0;
  }, [invoiceSummary, isInvoiceSummaryFetched, dashboardStats]);

  const getOutstandingInvoicesPage = async () => {
    const res = await axios.get(`invoice/outstanding`, {
      params: { limit: 8 },
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    return res?.data?.data;
  };

  const {
    data: outstandingInvoices,
    isFetching: isOutstandingInvoicesLoading,
    refetch: refetchOutstandingInvoices,
  } = useQuery(["outstandingInvoices"], getOutstandingInvoicesPage, {
    refetchOnWindowFocus: false,
    // Only DashboardUnpaidInvoices.js reads this — self-fetches on its own mount.
    // Dashboard.js's alert badge now sources its count from dashboardStats instead.
    enabled: false,
    // Long-lived: only a manual refresh, an invoice payment/create/delete (invalidated
    // explicitly), or a full page reload should refetch — not every dashboard nav-back.
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const getPriceList = async () => {
    try {
      const res = await axios.get(`pricelists`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      return res?.data || [];
    } catch (error) {
      console.error("Error fetching price list:", error);
      return [];
    }
  };
  const {
    data: priceList = [],
    refetch: refetchPriceList,
    isFetching: isPriceListLoading,
  } = useQuery(["priceList"], getPriceList, {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const getCheckList = async () => {
    try {
      const res = await axios.get(`check-list`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      return res?.data?.content || [];
    } catch (error) {
      console.error("Error fetching check list:", error);
      return [];
    }
  };
  const {
    data: checkList = [],
    refetch: refetchCheckList,
    isFetching: isCheckListLoading,
  } = useQuery(["checkList"], getCheckList, {
    refetchOnWindowFocus: false,
    enabled: false,
  });

  const getAccountsList = async (params) => fetchAccountsPage(params);

  const getPermissionList = async () => {
    const res = await axios.get(`${API_URL}/permissions`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    const rows = res?.data?.data || [];
    return rows.filter(
      (p) => typeof p?.name === "string" && p.name.includes(":") && p.isActive !== false
    );
  };

  const { data: permissionList = [], refetch: refetchPermissionList } = useQuery(
    ["permissions"],
    getPermissionList,
    {
      refetchOnWindowFocus: false,
      // Only Users > Add/Edit (AddUsers.js) reads this, and it already fetches on its own
      // mount — no need to pull the full permission catalog on every login.
      enabled: false,
    }
  );

  const getProvidersAccountsList = async (params) => {
    const { pageIndex, pageSize, search, sortBy, sortDirection } = params || {};
    const res = await axios.get(`accounts/provider/aggregated`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      params: {
        page: pageIndex ?? 0,
        size: pageSize ?? 20,
        search: search || undefined,
        sortBy: sortBy || undefined,
        sortDirection: sortDirection || "DESC",
      },
    });
    const raw = res?.data?.data;
    if (Array.isArray(raw)) {
      return { content: raw, totalItems: raw.length, summary: null };
    }
    return {
      content: Array.isArray(raw?.content) ? raw.content : [],
      totalItems: Number(raw?.totalItems) || 0,
      summary: raw?.summary || null,
    };
  };
  const {
    data: providerPage = { content: [], totalItems: 0, summary: null },
    isFetching: isProviderListLoading,
    refetch: refetchProvider,
  } = useQuery(
    ["accountsProvider", providerPagination],
    () => getProvidersAccountsList(providerPagination),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      // Only Accounts/index.js reads this — it already self-fetches via refetchProvider()
      // on its own mount. Not needed on every login/page.
      enabled: false,
    }
  );
  const providerList = providerPage.content;
  const providerTotalItems = providerPage.totalItems;
  const providerSummary = useMemo(() => {
    const s = providerPage.summary || {};
    return {
      commission: Number(s.totalCommission || 0),
      bonus: Number(s.totalBonus || 0),
      gst: Number(s.totalGST || 0),
      students: Number(s.totalStudents || 0),
      pendingClaims: Number(s.pendingClaimCount || 0),
      pendingCommission: Number(s.pendingCommission || 0),
    };
  }, [providerPage.summary]);

  const getIndividualProviderAccountData = (params) => {
    let url = `accounts/provider/${params}`;
    axios
      .get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        setProviderData(res?.data?.data);
        setProviderAccountDetails(
          res?.data?.data?.accountDetails?.map((item) => {
            const id = crypto.randomUUID();
            return {
              ...item,
              uuid: id,
              intakeDetails: item?.intakeDetails?.map((arg) => ({
                ...arg,
                parentId: id,
              })),
            };
          })
        );
      })
      .catch((err) => console.log(err));
  };

  const getIndividualAccountData = useCallback((condition, params, onFetched) => {
    const fetchSeq = ++accountFetchSeqRef.current;
    setAccountDataLoading(true);
    if (condition) {
      const url = `accounts/${params}`;
      axios
        .get(url, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => {
          if (fetchSeq !== accountFetchSeqRef.current) return;
          setAccountDataLoading(false);
          const apiData = res?.data?.data;
          // Fire before overwriting local state so caller can read pending
          // local data (e.g. unsaved notes) alongside the newly assigned IDs.
          onFetched?.(apiData);
          setAccountData(apiData);
          setAccountDetails(
            apiData?.accountDetails.map((item) => {
              // A row saved without a uuid is otherwise unreachable: the account editor addresses
              // rows by uuid, so "Manage terms" would do nothing. Derive a stable one from the id.
              const rowUuid = item.uuid || (item.id ? `cad-${item.id}` : crypto.randomUUID());
              return {
                ...item,
                uuid: rowUuid,
                admissionDetails: item?.admissionDetails?.map((arg) => ({
                  ...arg,
                  uuid: arg.uuid || (arg.id ? `sub-${arg.id}` : crypto.randomUUID()),
                  parentId: rowUuid,
                  notes: arg?.notes ?? [],
                })),
                notes: item?.notes ?? [],
              };
            })
          );
        })
        .catch((err) => {
          if (fetchSeq !== accountFetchSeqRef.current) return;
          console.log(err);
          setAccountDataLoading(false);
        });
    } else {
      setAccountDataLoading(false);
      setAccountData(null);
      setAccountDetails([createNewAccountDetail()]);
    }
  }, []);

  const resetAccountViewForNavigation = useCallback(() => {
    accountFetchSeqRef.current += 1;
    setAccountData(null);
    setAccountDetails([createNewAccountDetail()]);
    setAccountDataLoading(true);
  }, []);
  const { mutate: getPublicFormUrl, isLoading: publicUrlLoading } = useMutation({
    mutationFn: async () => {
      const orgId = JSON.parse(localStorage.getItem("userDetail"))?.orgId;
      if (!API_URL_PUBLIC) {
        throw new Error("Public API URL is not configured");
      }
      if (!orgId) {
        throw new Error("Organization id is not available");
      }

      const response = await axios.get(`${API_URL_PUBLIC}/admin/public-links/${orgId}`);
      const existingSlug = response.data?.data?.slug;
      if (existingSlug) return existingSlug;

      const createdResponse = await axios.post(`${API_URL_PUBLIC}/admin/public-links/${orgId}`);
      return createdResponse.data?.slug || createdResponse.data?.data?.slug;
    },
    enabled: isAdminOrSuperAdmin,
    onSuccess: (data) => {
      if (!data) {
        setPublicUrl("");
        setPublicUrlError("Public check-in link is not available for this organization.");
        return;
      }
      setPublicUrlError("");
      setPublicUrl(window.location.origin + "/client/check-in/" + data);
    },
    onError: (error) => {
      console.log(error);
      setPublicUrl("");
      setPublicUrlError(
        error?.response?.data?.message ||
          "Failed to load the public check-in link. Please check the configuration."
      );
    },
  });

  const getEmailTemplates = async () => {
    const response = await axios.get("/email-templates");
    return response?.data;
  };
  const {
    data: emailTemplates = [],
    isFetching: isEmailTemplatesLoading,
    refetch: refetchEmailTemplates,
  } = useQuery({
    queryKey: ["emailTemplates"],
    queryFn: getEmailTemplates,
    refetchOnWindowFocus: false,
    retry: false,
    // Only email-composer/template-manager surfaces read this — they self-fetch on
    // open/mount. Not needed on every login/page.
    enabled: false,
  });

  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  useEffect(() => {
    // Initial API calls when the token is available
    // Get fresh userDetail to ensure correct permissions check
    const currentUserDetail = getUserDetail();
    const currentIsAdminOrSuperAdmin =
      currentUserDetail?.type === "SUPER_ADMIN" || currentUserDetail?.type === "ADMIN";

    if (token) {
      // Do not invalidateQueries() here — it refetches every enabled query and
      // doubles API calls on load. Login/logout already clear the cache.
      if (currentIsAdminOrSuperAdmin) {
        refetchUsers();
        getPublicFormUrl();
        if (canReadReminders) {
          refetchReminderList();
        }
        if (canReadAppointments) {
          refetchAppointmentList();
        }
        // refetchNotificationData();
      }
    }
  }, [token]);

  // useEffect(() => {
  //   if (token) {
  //     refetchPaginatedClient({
  //       page: pagination.pageIndex,
  //       size: pagination.pageSize,
  //       searchText: pagination.searchText,
  //       caseOfficer: pagination.caseOfficer,
  //       createdMonth: pagination.createdMonth,
  //       createdYear: pagination.createdYear,
  //       sortBy: pagination.sortBy,
  //       sortDirection: pagination.sortDirection,
  //     });
  //   }
  // }, [token]);

  const state = {
    clientList,
    clientsVisaExpiring,
    isClientsVisaExpiringLoading,
    dashboardStats,
    isDashboardStatsLoading,
    paginatedClientList,
    consultancyList,
    isConsultancyListLoading,
    uniData,
    courseList,
    studentList,
    usersList,
    isUsersListLoading,
    invoiceList,
    invoiceSummary,
    isInvoiceSummaryLoading,
    outstandingInvoices,
    isOutstandingInvoicesLoading,
    token,
    rplList,
    archivedClientList,
    accountsList,
    accountsTotalItems: accountsTotalItemsResolved,
    selectedVisaTab,
    profitLossReport,
    isProfitLossLoading,
    notificationsList,
    isNotificationLoading,
    notificationClicked,
    accountData,
    providerList,
    providerTotalItems,
    providerSummary,
    providerPagination,
    isProviderListLoading,
    providerData,
    accountDetails,
    providerAccountDetails,
    combinedClientData,
    isUniLoading,
    rplCourseList,
    isRPLCourseListFetching,
    isStudentLoading,
    permissionList,
    accountDataLoading,
    priceList,
    isPriceListLoading,
    checkList,
    isCheckListLoading,
    checkInData,
    isCheckInDataLoading,
    isClientLoading,
    isPaginatedClientLoading,
    pagination,
    isAccountsListLoading,
    reminderList,
    isReminderLoading,
    notificationPagination,
    notificationPaginationForNavbar,
    notificationPaginationForAccounts,
    referralList,
    isReferralListLoading,
    referralClientsEntriesList,
    isReferralClientsEntriesListLoading,
    publicUrl,
    publicUrlError,
    publicUrlLoading,
    userDetail,
    notificationTriggered,
    isInvoiceListLoading,
    invoicePagination,
    emailTemplates,
    isEmailTemplatesLoading,
    accountPagination,
    notificationsListForNavbar,
    isNotificationLoadingForNavbar,
    notificationsListForAccounts,
    isNotificationLoadingForAccounts,
    unreadAccountNotificationsCount,
    outstandingInvoicesCount,
    clientViewTabByClientId,

    appointmentList,
    isAppointmentLoading,
  };

  const actions = {
    refetchClient,
    refetchClientsVisaExpiring,
    refetchDashboardStats,
    refetchPaginatedClient,
    refetchConsultancy,
    refetchUniData,
    refetchCourseList,
    refetchStudent,
    refetchUsers,
    refetchInvoiceList,
    fetchInvoiceList,
    refetchAccountList,
    // getRPLList,
    setToken,
    setRPLList,
    setSelectedVisaTab,
    getProfitLossReport,
    refetchNotificationData,
    setNotificationClicked,
    setAccountData,
    getIndividualAccountData,
    accountFetchSeqRef,
    resetAccountViewForNavigation,
    getIndividualProviderAccountData,
    refetchProvider,
    setProviderPagination,
    setProviderData,
    setAccountDetails,
    setProviderAccountDetails,
    setCombinedClientData,
    refetchRPLCourseList,
    refetchPermissionList,
    setAccountDataLoading,
    refetchPriceList,
    refetchCheckInData,
    setPagination,
    getReminderData,
    refetchReminderList,
    setNotificationPagination,
    refetchReferral,
    refetchReferralClientsEntries,
    getPublicFormUrl,
    refetchCheckList,
    setNotificationTriggered,
    refetchNotifications,
    refetchEmailTemplates,
    refetchArchivedClient,
    setInvoicePagination,
    setAccountPagination,
    setIsAccountsPageActive,
    setIsNotificationsPageActive,
    refetchNotificationDataForNavbar,
    refetchNotificationsForNavbar,
    refetchNotificationDataForAccounts,
    refetchNotificationsForAccounts,
    refetchInvoiceSummary,
    refetchOutstandingInvoices,
    setClientViewTab,
    refetchAppointmentList,
  };

  return [state, actions];
};
export default useStateAndActions;
